import { Fetcher } from '@this/src/util';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { Popover, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import type OrderItemStepTodo from '@this/src/domain/arrangement/order_item_step_todo';
import type Arranger from '@this/src/domain/arranger/arranger';
import { styled } from '@this/src/components/constants/themes';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';

interface Props {
  todo: OrderItemStepTodo | null;
  anchorEl: Element | null;
  onChange: (todo: OrderItemStepTodo, arranger: Arranger | null) => void;
  onClose: () => void;
}

interface State {
  arrangers: Arranger[];
  selectedArranger: Arranger | null;
  query: string;
  modalOpen: boolean;
}

interface Request extends State {
  setState: Dispatch<SetStateAction<State>>;
}

interface Response {
  arrangers: Arranger[];
}

const fetchMasterStepArrangers = ({ arrangers, selectedArranger, query, setState }: Request) => {
  const params = {
    q: query,
    ids: selectedArranger ? [selectedArranger.id] : []
  };
  Fetcher.get<Response>('/arrangement/arrangers/autocomplete.json', params).then(res => {
    if (res.arrangers.map(mss => mss.id).join(',') !== arrangers.map(mss => mss.id).join(',')) {
      setState(state => ({ ...state, arrangers: res.arrangers }));
    }
  });
};

const OrderItemStepArrangerUpdateSelector: React.FC<Props> = ({ todo, anchorEl, onChange, onClose }) => {
  const [state, setState] = useState<State>({
    arrangers: [],
    selectedArranger: null,
    query: '',
    modalOpen: false
  });
  const { arrangers, selectedArranger, query, modalOpen } = state;

  useEffect(() => {
    fetchMasterStepArrangers({ ...state, setState });
  }, [query, selectedArranger]);

  useEffect(() => {
    if (todo) {
      setState({
        ...state,
        selectedArranger: state.arrangers.find(mss => mss.id === todo.assignedToId) || null
      });
    }
  }, [todo]);

  const handleClose = useCallback(() => {
    onClose();
    setState(state => ({ ...state, query: '' }));
  }, [onClose]);

  const handleModalClose = useCallback(() => {
    setState(state => ({ ...state, modalOpen: false }));
  }, []);

  const handleChange = useCallback((_event: unknown, value: Arranger | null) => {
    setState(state => ({ ...state, selectedArranger: value }));
  }, []);

  const handleInputChange = useCallback((_event: unknown, value: string) => {
    setState(state => ({ ...state, query: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!todo) return;
    if (!['queue', 'todo', 'started', 'suspended'].includes(todo.stepStatus)) return;

    setState(state => {
      if (!state.modalOpen && ['started', 'suspended'].includes(todo.stepStatus)) {
        return { ...state, modalOpen: true };
      }

      onChange(todo, state.selectedArranger);
      onClose();
      return { ...state, query: '', modalOpen: false };
    });
  }, [todo, onChange, onClose]);

  return (
    <>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Content>
          <Autocomplete
            options={arrangers}
            getOptionLabel={getOptionLabel}
            filterOptions={filterOptions}
            onChange={handleChange}
            onInputChange={handleInputChange}
            inputValue={query}
            value={selectedArranger}
            size="small"
            renderInput={renderInput}
            style={{ minWidth: '240px', maxWidth: '90%' }}
          />
          <Actions>
            <Button size="small" color="sub" onClick={handleClose}>
              キャンセル
            </Button>
            <Button size="small" color="primary" onClick={handleSubmit}>
              保存
            </Button>
          </Actions>
        </Content>
      </Popover>
      <Modal open={modalOpen} size="medium" onClose={handleModalClose}>
        <ModalHeader>担当者の変更</ModalHeader>
        <ModalBody>
          <p>{todo?.assignedToName}さんが現在着手しています。強制的に終了しますか？</p>
        </ModalBody>
        <ModalFooter>
          <Button color="sub" onClick={handleModalClose}>
            キャンセル
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            保存
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const getOptionLabel = (mss: Arranger) => mss.name ?? '';

const filterOptions = (options: Arranger[], state: { inputValue: string }) => {
  const q: string = state.inputValue;
  return options.filter(mss => mss.name && mss.name.toLocaleLowerCase().indexOf(q.toLocaleLowerCase()) > -1);
};

const renderInput = (params: any) => (
  <TextField {...params} variant="outlined" style={{}} InputLabelProps={{ shrink: true }} size="small" />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

export default OrderItemStepArrangerUpdateSelector;
